export type Modality = "image" | "video" | "both";
export type ModelStatus = "ga" | "preview" | "deprecated" | "beta";
export type PriceUnit =
  | "per_image"
  | "per_second"
  | "per_megapixel"
  | "per_1m_tokens"
  | "per_credit"
  | "per_request"
  | "neuron"
  | "unknown";
export type PriceConfidence = "verified" | "estimated" | "unknown";
/** SOTA | Good | Usable — catalog quality rank for filtering/badges */
export type QualityRank = "sota" | "good" | "usable";

export interface Provider {
  id: string;
  name: string;
  website: string;
  docsUrl: string;
  pricingUrl: string;
  description: string;
  /** Simple Icons slug, e.g. openai → https://cdn.simpleicons.org/openai */
  iconSlug?: string;
  regions?: string[];
  notes?: string;
  /** Partner hosts / aggregator metadata (e.g. Pruna partners) */
  partnerHosts?: string[];
}

export interface PriceTier {
  label: string;
  amountUsd: number | null;
  unit: PriceUnit;
  notes?: string;
}

export interface ModelPricing {
  raw: string;
  /** Structured raw tiers as scraped / researched */
  tiers: PriceTier[];
  /** Alias of tiers for compare payloads */
  rawPricing?: PriceTier[];
  normalizedUsd: number | null;
  normalizedUnit: "per_image" | "per_mp" | "per_second" | "unknown";
  /** How normalizedUsd was derived (resolution, duration, MP assumptions) */
  assumptions?: string;
  confidence: PriceConfidence;
  scrapedAt?: string;
  sourceUrl?: string;
}

export interface ModelLimits {
  maxResolution?: string;
  maxDurationSec?: number;
  rateLimitNote?: string;
}

/** Structured capability matrix beyond free-form tags */
export interface CapabilityStrengths {
  edit?: boolean;
  lora?: boolean;
  textToImage?: boolean;
  imageToImage?: boolean;
  textToVideo?: boolean;
  imageToVideo?: boolean;
  audioInVideo?: boolean;
  characterReference?: boolean;
  controlnet?: boolean;
  upscale?: boolean;
  inpaint?: boolean;
  outpaint?: boolean;
  styleReference?: boolean;
  moodboard?: boolean;
  training?: boolean;
}

export const STRENGTH_KEYS: (keyof CapabilityStrengths)[] = [
  "textToImage",
  "imageToImage",
  "edit",
  "inpaint",
  "outpaint",
  "upscale",
  "lora",
  "controlnet",
  "styleReference",
  "moodboard",
  "characterReference",
  "textToVideo",
  "imageToVideo",
  "audioInVideo",
  "training",
];

export const STRENGTH_LABELS: Record<keyof CapabilityStrengths, string> = {
  edit: "Edit",
  lora: "LoRA",
  textToImage: "Text→Image",
  imageToImage: "Image→Image",
  textToVideo: "Text→Video",
  imageToVideo: "Image→Video",
  audioInVideo: "Audio in video",
  characterReference: "Character ref",
  controlnet: "ControlNet",
  upscale: "Upscale",
  inpaint: "Inpaint",
  outpaint: "Outpaint",
  styleReference: "Style ref",
  moodboard: "Moodboard",
  training: "Training",
};

export interface Model {
  id: string;
  name: string;
  providerId: string;
  modality: Modality;
  status: ModelStatus;
  pricing: ModelPricing;
  limits?: ModelLimits;
  capabilities: string[];
  /** Structured strengths for matrix UI / filters */
  strengths?: CapabilityStrengths;
  /** Quality reputation band */
  qualityRank?: QualityRank;
  docsUrl: string;
  lastVerified: string;
  notes?: string;
  aliases?: string[];
  /** fal.ai endpoint id when known */
  endpointId?: string;
  /** Merged from KV community scoring */
  communityScore?: number | null;
  caveats?: string[];
  caveatsUpdatedAt?: string;
  caveatsSampleSize?: number;
  /** Set when confidence came from Jev override */
  confidenceSource?: "seed" | "jev" | "refresh";
  /** Origin of row: seed catalog or fal sync */
  source?: "seed" | "fal" | "replicate";
}

export interface CatalogMeta {
  lastUpdated: string;
  disclaimer: string;
  lastRefreshAt?: string | null;
  lastPriceScrapeAt?: string | null;
  lastCaveatsAt?: string | null;
  lastRescoreAt?: string | null;
  lastRefreshNote?: string;
  lastFalSyncAt?: string | null;
  lastXCaveatsAt?: string | null;
  falModelCount?: number;
  lastReplicateSyncAt?: string | null;
  replicateModelCount?: number;
  lastCatalogSyncAt?: string | null;
  lastCatalogSyncNote?: string;
  priceScrapeSummary?: string;
}
